import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimatedSection from "@/components/AnimatedSection";
import CountUp from "@/components/CountUp";
import { getProjectBySlug, getRelatedProjects } from "@/mocks/projects";
import type { ProjectGalleryImage } from "@/mocks/projects";

function parseMetricValue(raw: string): { numeric: number; suffix: string } {
  const cleaned = raw.replace(/[,<>\s]/g, "");
  const match = cleaned.match(/^(\d+\.?\d*)(.*)$/);
  if (match) {
    return { numeric: parseFloat(match[1]), suffix: match[2] };
  }
  return { numeric: 0, suffix: raw };
}

function GalleryLightbox({ images, currentIndex, onClose, onPrev, onNext }: { images: ProjectGalleryImage[]; currentIndex: number; onClose: () => void; onPrev: () => void; onNext: () => void }) {
  const image = images[currentIndex];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer z-10"
      >
        <i className="ri-close-line text-xl" />
      </button>
      {currentIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-3 md:left-8 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer z-10"
        >
          <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
        </button>
      )}
      {currentIndex < images.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-3 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer z-10"
        >
          <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
        </button>
      )}
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt}
          className="max-w-full max-h-[75vh] object-contain rounded-lg"
        />
        <p className="text-white/80 text-sm mt-4 text-center max-w-lg">{image.caption}</p>
        <p className="text-white/40 text-xs mt-1">{currentIndex + 1} / {images.length}</p>
      </motion.div>
    </motion.div>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const project = slug ? getProjectBySlug(slug) : undefined;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900">Project Not Found</h1>
          <p className="text-gray-500 mt-4 mb-8">The project you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent/90 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line" />
            Back to Home
          </Link>
        </section>
        <Footer />
      </div>
    );
  }

  const relatedProjects = getRelatedProjects(project.id, 2);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-20 relative overflow-hidden" style={{ minHeight: "420px" }}>
        <div className="absolute inset-0">
          <img
            src={project.heroImageUrl}
            alt={project.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-4xl"
          >
            <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-white/10 text-white border border-white/20 whitespace-nowrap backdrop-blur-sm">
                {project.sector}
              </span>
              <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-white/10 text-white border border-white/20 whitespace-nowrap backdrop-blur-sm">
                {project.completionDate}
              </span>
              <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-white/10 text-white border border-white/20 whitespace-nowrap backdrop-blur-sm">
                <i className="ri-map-pin-line mr-1" />
                {project.location}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">{project.title}</h1>
            <p className="text-white/80 text-base md:text-lg max-w-2xl leading-relaxed">{project.heroDescription}</p>
            <div className="flex items-center gap-3 mt-6">
              <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <i className="ri-building-4-line text-white text-lg" />
              </div>
              <div>
                <p className="text-white/70 text-xs uppercase tracking-wide">Client</p>
                <p className="text-white font-semibold text-sm">{project.client}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Metrics Banner */}
      <section className="py-8 md:py-10 bg-primary">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {project.metrics.map((metric, index) => {
                const { numeric, suffix } = parseMetricValue(metric.value);
                return (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-2xl md:text-3xl font-bold text-accent">
                  <CountUp end={numeric} duration={2} suffix={suffix} />
                </div>
                <p className="text-white/70 text-xs md:text-sm mt-1">{metric.label}</p>
              </motion.div>
                );
              })}
          </div>
        </div>
      </section>

      {/* Overview & Challenge/Solution */}
      <section className="py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
            <AnimatedSection direction="left">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-1 bg-accent rounded-full" />
                  <span className="text-accent font-semibold text-sm uppercase tracking-wide">Overview</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                  Project Overview
                </h2>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">{project.overview}</p>
              </div>
            </AnimatedSection>
            <AnimatedSection direction="right">
              <div className="rounded-2xl overflow-hidden border border-gray-100 aspect-[4/3] w-full">
                <img
                  src={project.overviewImageUrl}
                  alt={project.title}
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-14 md:py-20 bg-gray-50">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <AnimatedSection direction="left">
              <motion.div
                whileHover={{ y: -4, transition: { duration: 0.25 } }}
                className="p-6 md:p-8 rounded-xl bg-white border border-gray-100 h-full"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-xl" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">The Challenge</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{project.challenge}</p>
              </motion.div>
            </AnimatedSection>
            <AnimatedSection direction="right">
              <motion.div
                whileHover={{ y: -4, transition: { duration: 0.25 } }}
                className="p-6 md:p-8 rounded-xl bg-white border border-gray-100 h-full"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-green-500 text-xl" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">Our Solution</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{project.solution}</p>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-1 bg-accent rounded-full" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">Key Features</span>
                <div className="w-10 h-1 bg-accent rounded-full" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">What We Delivered</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">The core capabilities that make this deployment exceptional.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {project.features.map((feature, index) => (
              <AnimatedSection key={feature.title} delay={index * 0.08}>
                <motion.div
                  whileHover={{ y: -5, transition: { duration: 0.25 } }}
                  className="p-6 rounded-xl bg-white border border-gray-100 hover:border-accent/30 transition-all duration-300 h-full"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center mb-4 flex-shrink-0">
                    <i className={`${feature.icon} text-accent text-lg`} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-14 md:py-20 bg-gray-50">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-1 bg-accent rounded-full" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">Technology Stack</span>
                <div className="w-10 h-1 bg-accent rounded-full" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Technologies Deployed</h2>
            </div>
          </AnimatedSection>
          <div className="flex flex-wrap justify-center gap-3">
            {project.technologies.map((tech, index) => (
              <AnimatedSection key={tech} delay={index * 0.05}>
                <span className="px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-gray-200 text-gray-700 hover:border-accent/30 hover:text-accent transition-all duration-300 whitespace-nowrap cursor-default">
                  {tech}
                </span>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Gallery */}
      <section className="py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-1 bg-accent rounded-full" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">Gallery</span>
                <div className="w-10 h-1 bg-accent rounded-full" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Installation Photos</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">Behind-the-scenes and completed installation photography from this project.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {project.galleryImages.map((img, index) => (
              <AnimatedSection key={img.id} delay={index * 0.06}>
                <motion.div
                  whileHover={{ y: -4, transition: { duration: 0.25 } }}
                  className="group rounded-xl overflow-hidden border border-gray-100 bg-white hover:shadow-lg transition-all duration-300 cursor-pointer"
                  onClick={() => setLightboxIndex(index)}
                >
                  <div className="aspect-[3/2] overflow-hidden relative">
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <div>
                        <p className="text-white text-sm font-medium">{img.caption}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <i className="ri-zoom-in-line text-white/70 text-xs" />
                          <span className="text-white/60 text-xs">Click to enlarge</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <AnimatePresence>
          <GalleryLightbox
            images={project.galleryImages}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onPrev={() => setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev))}
            onNext={() => setLightboxIndex((prev) => (prev !== null && prev < project.galleryImages.length - 1 ? prev + 1 : prev))}
          />
        </AnimatePresence>
      )}

      {/* Results */}
      <section className="py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-1 bg-accent rounded-full" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">Results</span>
                <div className="w-10 h-1 bg-accent rounded-full" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Measurable Impact</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">Verified outcomes tracked post-deployment.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {project.results.map((result, index) => (
              <AnimatedSection key={result.title} delay={index * 0.12}>
                <motion.div
                  whileHover={{ y: -4, transition: { duration: 0.25 } }}
                  className="p-6 rounded-xl bg-white border border-gray-100 hover:border-accent/20 transition-all duration-300 h-full"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-green-500 text-lg" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800 mb-2">{result.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{result.description}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-14 md:py-20 bg-gray-50">
          <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-10 h-1 bg-accent rounded-full" />
                  <span className="text-accent font-semibold text-sm uppercase tracking-wide">More Projects</span>
                  <div className="w-10 h-1 bg-accent rounded-full" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Explore Other Projects</h2>
              </div>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto">
              {relatedProjects.map((related, index) => (
                <AnimatedSection key={related.id} delay={index * 0.1}>
                  <Link to={`/projects/${related.slug}`}>
                    <motion.div
                      whileHover={{ y: -6, transition: { duration: 0.25 } }}
                      className="p-6 rounded-xl bg-white border border-gray-100 hover:border-accent/30 transition-all duration-300 h-full cursor-pointer"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-4-line text-accent text-lg" />
                        </div>
                        <div>
                          <p className="text-accent text-xs font-semibold uppercase tracking-wide">{related.sector}</p>
                          <p className="text-gray-500 text-xs">{related.location}</p>
                        </div>
                      </div>
                      <h3 className="text-base font-bold text-gray-800 mb-2">{related.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{related.heroDescription}</p>
                      <div className="flex items-center gap-1.5 text-accent text-sm font-medium mt-4 whitespace-nowrap">
                        View project details
                        <i className="ri-arrow-right-line text-xs" />
                      </div>
                    </motion.div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-14 md:py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/8 via-transparent to-primary" />
        <div className="absolute -top-32 -right-32 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-5">{project.ctaText}</h2>
            <p className="text-white/80 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Let us design and deploy a similar intelligent AV solution tailored to your organisation's unique requirements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3.5 bg-accent text-white font-semibold text-sm rounded-lg hover:bg-accent/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Start Your Project
                </motion.button>
              </Link>
              <Link to="/solutions">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3.5 bg-white/10 text-white font-semibold text-sm rounded-lg border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Explore Our Solutions
                </motion.button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}